import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import { ICONS } from "../../constants/icons"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import useAdminPermission from "../../hooks/useAdminPermission"
import { ADMIN_NAV_ITEMS, ADMIN_SHORTCUTS } from "./adminNav"
import { toggleAdminSidebar } from "./sidebarToggle"

const NAV_SKELETON_COUNT = 8

const SidebarNavSkeleton = () => (
  <div className="admin-sidebar__nav-skeleton" aria-hidden="true">
    {Array.from({ length: NAV_SKELETON_COUNT }).map((_, index) => (
      <div
        key={index}
        className="admin-sidebar__item admin-sidebar__item--skeleton"
      />
    ))}
  </div>
)

const SidebarContent = () => {
  const location = useLocation()
  const { can, canNav, isReady, isSuperAdmin } = useAdminPermission()

  const navItems = useMemo(() => {
    if (!isReady && !isSuperAdmin) return []
    // Sidebar items require VIEW — create/edit/delete alone are not enough
    return ADMIN_NAV_ITEMS.filter(item => canNav(item.key))
  }, [canNav, isReady, isSuperAdmin])

  const shortcuts = useMemo(() => {
    if (!isReady && !isSuperAdmin) return []
    return ADMIN_SHORTCUTS.filter(item => {
      if (item.module && !can(item.module, item.action || "create")) {
        return false
      }
      const viewModules = Array.isArray(item.requiresView)
        ? item.requiresView
        : item.requiresView
          ? [item.requiresView]
          : []
      if (viewModules.some(module => !can(module, "view"))) {
        return false
      }
      return true
    })
  }, [can, isReady, isSuperAdmin])

  if (!isReady && !isSuperAdmin) {
    return <SidebarNavSkeleton />
  }

  return (
    <div className="admin-sidebar__nav">
      <nav className="admin-sidebar__menu" aria-label="Admin navigation">
        {navItems.map(item => {
          const isActive =
            location.pathname === item.to ||
            location.pathname.startsWith(`${item.to}/`)

          return (
            <Link
              key={item.key}
              to={item.to}
              className={`admin-sidebar__item${
                isActive ? " admin-sidebar__item--active" : ""
              }`}
            >
              <span className="admin-sidebar__item-icon" aria-hidden="true">
                <img src={item.icon} alt="" />
              </span>
              <span className="admin-sidebar__item-label">{item.label}</span>
            </Link>
          )
        })}
      </nav>

      {shortcuts.length > 0 && (
        <>
          <div className="admin-sidebar__divider" aria-hidden="true" />

          <div className="admin-sidebar__shortcuts">
            <p className="admin-sidebar__shortcuts-title">Shortcuts</p>
            <div className="admin-sidebar__shortcuts-list">
              {shortcuts.map(item => (
                <Link
                  key={item.key}
                  to={item.to}
                  className="admin-sidebar__shortcut"
                >
                  <span
                    className="admin-sidebar__shortcut-icon"
                    aria-hidden="true"
                  >
                    <img src={ICONS.adminAddCircle} alt="" />
                  </span>
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const getAdminRoleLabel = user => {
  if (user?.type === "super_admin") return "Super Admin"

  const roleNames = (Array.isArray(user?.roles) ? user.roles : [])
    .map(role => role?.name)
    .filter(Boolean)

  if (roleNames.length > 0) return roleNames.join(", ")
  return "Admin"
}

const Sidebar = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const currentUser = useSelector(state => state.auth.currentUser)
  const profileRef = React.useRef(null)

  const displayName = getUserFullName(currentUser)
  const roleLabel = getAdminRoleLabel(currentUser)
  const avatarSrc = getUserAvatarUrl(currentUser)
  const avatarName = displayName || roleLabel || "Admin"

  useEffect(() => {
    if (!menuOpen) return undefined
    const handleClick = e => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    const handleKey = e => {
      if (e.key === "Escape") setMenuOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      document.removeEventListener("keydown", handleKey)
    }
  }, [menuOpen])

  const toggleSidebar = () => {
    toggleAdminSidebar()
  }

  const toggleProfileMenu = () => {
    setMenuOpen(open => !open)
  }

  return (
    <aside className="admin-sidebar">
      <div className="admin-sidebar__top">
        <div className="admin-sidebar__brand">
          <span className="admin-sidebar__logo">AMOK.EARTH</span>
          <button
            type="button"
            className="admin-sidebar__toggle"
            onClick={toggleSidebar}
            aria-label="Toggle sidebar"
          >
            <img src={ICONS.adminGrid4} alt="" />
          </button>
        </div>

        <div className="admin-sidebar__scroll">
          <SidebarContent />
        </div>
      </div>

      <div className="admin-sidebar__bottom">
        <div
          className={`admin-sidebar__profile${
            menuOpen ? " admin-sidebar__profile--menu-open" : ""
          }`}
          ref={profileRef}
        >
          <button
            type="button"
            className="admin-sidebar__profile-card"
            onClick={toggleProfileMenu}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label="Open profile menu"
            title={roleLabel}
          >
            <span className="admin-sidebar__profile-link">
              <Avatar
                src={avatarSrc}
                name={avatarName}
                size="md"
                className="admin-sidebar__avatar"
              />
              <span className="admin-sidebar__profile-meta">
                <span className="admin-sidebar__profile-role">{roleLabel}</span>
                <span className="admin-sidebar__profile-name">
                  {displayName || "Admin"}
                </span>
              </span>
            </span>
            <span
              className="admin-sidebar__profile-menu-dots"
              aria-hidden="true"
            >
              <span />
              <span />
              <span />
            </span>
          </button>

          {menuOpen && (
            <div className="admin-sidebar__profile-menu" role="menu">
              <Link
                to="/admin/settings"
                className="admin-sidebar__profile-menu-item"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                Settings
              </Link>
              <Link
                to="/logout"
                className="admin-sidebar__profile-menu-item"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                Logout
              </Link>
            </div>
          )}
        </div>

        <p className="admin-sidebar__copyright">
          &copy; Amok Earth 2026. All Rights Reserved.
        </p>
      </div>
    </aside>
  )
}

export default Sidebar
